"use client";

import React, { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import {
  Search,
  X,
  ArrowRight,
  Sparkles,
  Layers,
  Code2,
  Cpu,
  Smartphone,
  Globe,
  Database,
  Briefcase,
  FileText,
  DollarSign,
  Phone,
  MessageSquare,
  Sun,
  Moon,
  ExternalLink,
  History,
  CornerDownLeft,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { EmptyState } from "./EmptyState";
import { useScrollLock } from "@/hooks/useScrollLock";

export interface SearchItem {
  id: string;
  title: string;
  description: string;
  category: "Demos" | "Services" | "Pages" | "Actions";
  href?: string;
  action?: () => void;
  icon: React.ReactNode;
  tags: string[];
}

export function CommandKSearch() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Lock background body scroll when Command+K popup is open
  useScrollLock(isOpen);

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem("johnson_recent_searches");
      if (saved) setRecentSearches(JSON.parse(saved));
    } catch {
      // Ignore
    }
  }, []);

  // Global Command+K and Escape keyboard shortcut listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      } else if (e.key === "Escape" && isOpen) {
        e.preventDefault();
        setIsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setSelectedIndex(0);
    } else {
      setQuery("");
    }
  }, [isOpen]);

  // Search Catalog
  const catalog: SearchItem[] = [
    // Live Industry Demos
    {
      id: "demo-real-estate",
      title: "Real Estate Pipeline CRM & Luxury Assets Demo",
      description: "Sub-15s speed-to-lead, automated floor plans, and 5-stage broker Kanban.",
      category: "Demos",
      href: "/demos/real-estate",
      icon: <Briefcase className="w-4 h-4 text-emerald-500" />,
      tags: ["property", "broker", "luxury", "kanban", "housing", "floor plan"],
    },
    {
      id: "demo-restaurant",
      title: "Restaurant Table & Guest Booking Hub Demo",
      description: "Direct WhatsApp reservations, 0% cover commissions, and floor CRM.",
      category: "Demos",
      href: "/demos/restaurant",
      icon: <Sparkles className="w-4 h-4 text-amber-500" />,
      tags: ["dining", "table", "food", "reservation", "hospitality", "menu"],
    },
    {
      id: "demo-gym",
      title: "IronPulse Fitness & Gym Club CRM Demo",
      description: "Automated renewal alerts at 7, 3, & 1 day, reducing member drop-offs by 40%.",
      category: "Demos",
      href: "/demos/gym",
      icon: <Layers className="w-4 h-4 text-blue-500" />,
      tags: ["fitness", "workout", "membership", "qr access", "attendance"],
    },
    {
      id: "demo-dental",
      title: "Dental & Medical Clinic Patient Recall Demo",
      description: "Multi-chair doctor scheduler, automated recall cadences, and HIPAA compliance.",
      category: "Demos",
      href: "/demos/dental",
      icon: <Cpu className="w-4 h-4 text-cyan-500" />,
      tags: ["clinic", "doctor", "health", "patient", "recall", "chair"],
    },
    {
      id: "demo-telecalling",
      title: "High-Velocity Telecalling & Dialer CRM Demo",
      description: "1-click direct dialing queue, candidate verification, and 60% rep speed boost.",
      category: "Demos",
      href: "/demos/telecalling",
      icon: <Phone className="w-4 h-4 text-purple-500" />,
      tags: ["telecall", "dialer", "call center", "bpo", "driver", "onboarding"],
    },
    {
      id: "demo-insurance",
      title: "Insurance Advisory & Policy Vault CRM Demo",
      description: "Encrypted policy vault, automated annual renewal reminders, and claims tracking.",
      category: "Demos",
      href: "/demos/insurance",
      icon: <Database className="w-4 h-4 text-rose-500" />,
      tags: ["policy", "motor", "health", "life", "kyc", "advisory"],
    },
    {
      id: "demo-hiring",
      title: "Staffing & Executive Recruitment CRM Demo",
      description: "Candidate evaluation passes, confidential talent mapping, and headhunter pipeline.",
      category: "Demos",
      href: "/demos/hiring",
      icon: <Briefcase className="w-4 h-4 text-indigo-500" />,
      tags: ["recruitment", "headhunter", "candidates", "interview", "resume"],
    },
    {
      id: "demo-legal",
      title: "Law Firms & Legal Chambers CRM Demo",
      description: "BCI Rule 36 compliant legal intake, live CNR case tracking, and NDOH calendar.",
      category: "Demos",
      href: "/demos/legal",
      icon: <FileText className="w-4 h-4 text-amber-600" />,
      tags: ["law", "advocate", "court", "cnr", "chambers", "bar council"],
    },

    // Services
    {
      id: "service-custom-software",
      title: "Custom Software Engineering",
      description: "End-to-end bespoke software architecture built for enterprise scale.",
      category: "Services",
      href: "/custom-software-development",
      icon: <Code2 className="w-4 h-4 text-blue-500" />,
      tags: ["bespoke", "architecture", "enterprise", "backend", "full stack"],
    },
    {
      id: "service-web-apps",
      title: "Web Application Development",
      description: "Ultra-fast Next.js, React, and Node.js cloud applications.",
      category: "Services",
      href: "/web-application-development",
      icon: <Globe className="w-4 h-4 text-cyan-500" />,
      tags: ["nextjs", "react", "frontend", "spa", "pwa", "fullstack"],
    },
    {
      id: "service-mobile-apps",
      title: "Mobile App Engineering (iOS & Android)",
      description: "Native and cross-platform mobile apps with zero UI latency.",
      category: "Services",
      href: "/mobile-app-development",
      icon: <Smartphone className="w-4 h-4 text-emerald-500" />,
      tags: ["react native", "flutter", "ios", "android", "apps"],
    },
    {
      id: "service-ai-agents",
      title: "AI Automation & Autonomous Agents",
      description: "LLM agents, WhatsApp autonomous reps, and business workflow automation.",
      category: "Services",
      href: "/ai-automation-agents",
      icon: <Sparkles className="w-4 h-4 text-purple-500" />,
      tags: ["ai", "agents", "llm", "whatsapp bot", "autonomous", "chatbots"],
    },
    {
      id: "service-crm-erp",
      title: "CRM & ERP Systems Integration",
      description: "Custom pipeline management, sales automation, and zero SaaS tax.",
      category: "Services",
      href: "/crm-erp-integration",
      icon: <Database className="w-4 h-4 text-amber-500" />,
      tags: ["crm", "erp", "pipeline", "saas tax", "single tenant"],
    },

    // Pages & Resources
    {
      id: "page-pricing",
      title: "Transparent Pricing & Architecture Retainers",
      description: "Zero per-seat SaaS taxes, transparent one-time turnkey fees, and SLA guarantees.",
      category: "Pages",
      href: "/pricing",
      icon: <DollarSign className="w-4 h-4 text-emerald-500" />,
      tags: ["cost", "packages", "quote", "retainer", "fees", "inr", "usd"],
    },
    {
      id: "page-estimator",
      title: "Project Cost & Timeline Estimator",
      description: "Interactive calculator scoping development budgets and deployment timelines.",
      category: "Pages",
      href: "/resources/project-estimator",
      icon: <Layers className="w-4 h-4 text-blue-500" />,
      tags: ["calculator", "budget", "timeline", "estimate", "scope"],
    },
    {
      id: "page-case-studies",
      title: "Enterprise Case Studies & Architecture Proof",
      description: "Verified customer implementations across logistics, healthcare, and real estate.",
      category: "Pages",
      href: "/case-studies",
      icon: <FileText className="w-4 h-4 text-slate-500" />,
      tags: ["portfolio", "proof", "clients", "results", "roi"],
    },
    {
      id: "page-pitch",
      title: "Executive Pitch Deck & Platform Architecture",
      description: "Full slide presentation on our engineering stack and business impact.",
      category: "Pages",
      href: "/pitch",
      icon: <Layers className="w-4 h-4 text-indigo-500" />,
      tags: ["slides", "investor", "deck", "architecture", "overview"],
    },
    {
      id: "page-contact",
      title: "Book Free Engineering Consultation",
      description: "Direct scoping call with our lead solutions architects.",
      category: "Pages",
      href: "/contact",
      icon: <Phone className="w-4 h-4 text-cyan-500" />,
      tags: ["consultation", "call", "schedule", "audit", "quote"],
    },

    // Quick Actions
    {
      id: "action-whatsapp",
      title: "Start Direct WhatsApp Discussion",
      description: "Chat with Lead Architect on WhatsApp (+91 98808 93416).",
      category: "Actions",
      action: () => {
        window.open("https://wa.me/919880893416?text=Hi%20Johnson%20Softwares,%20I'm%20exploring%20your%20services.", "_blank");
      },
      icon: <MessageSquare className="w-4 h-4 text-emerald-500" />,
      tags: ["whatsapp", "chat", "direct", "message"],
    },
    {
      id: "action-call",
      title: "Call Founder Johnson Directly",
      description: "+91 98808 93416 (Available 9:00 AM - 8:00 PM IST)",
      category: "Actions",
      action: () => {
        window.location.href = "tel:+919880893416";
      },
      icon: <Phone className="w-4 h-4 text-blue-500" />,
      tags: ["phone", "call", "mobile", "founder"],
    },
  ];

  // Filter items based on query and category
  const filteredItems = catalog.filter((item) => {
    const matchesCategory = selectedCategory === "All" || item.category === selectedCategory;
    if (!matchesCategory) return false;

    if (!query.trim()) return true;

    const q = query.toLowerCase().trim();
    const titleMatch = item.title.toLowerCase().includes(q);
    const descMatch = item.description.toLowerCase().includes(q);
    const tagMatch = item.tags.some((t) => t.toLowerCase().includes(q));

    return titleMatch || descMatch || tagMatch;
  });

  // Handle keyboard selection (arrows, Enter)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % Math.max(1, filteredItems.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filteredItems.length) % Math.max(1, filteredItems.length));
    } else if (e.key === "Enter" && filteredItems.length > 0) {
      e.preventDefault();
      const selected = filteredItems[selectedIndex];
      if (selected) executeItem(selected);
    }
  };

  const executeItem = (item: SearchItem) => {
    // Save to recents
    if (query.trim()) {
      const updated = [query.trim(), ...recentSearches.filter((s) => s !== query.trim())].slice(0, 5);
      setRecentSearches(updated);
      localStorage.setItem("johnson_recent_searches", JSON.stringify(updated));
    }

    setIsOpen(false);

    if (item.action) {
      item.action();
    } else if (item.href) {
      router.push(item.href);
    }
  };

  return (
    <>
      {/* Global Command Dialog Modal */}
      <AnimatePresence>
        {isOpen && (
          <div
            className="fixed inset-0 z-[100] flex items-start justify-center pt-16 sm:pt-24 px-4 print:hidden overscroll-contain"
            onTouchMove={(e) => {
              if (e.target === e.currentTarget) e.preventDefault();
            }}
          >
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              onTouchMove={(e) => e.preventDefault()}
              className="fixed inset-0 bg-slate-950/70 backdrop-blur-md overscroll-contain"
            />

            {/* Dialog Container */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: -10 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="relative w-full max-w-2xl rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl overflow-hidden z-10 overscroll-contain"
            >
              {/* Search Input Bar */}
              <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-200 dark:border-slate-800">
                <Search className="w-5 h-5 text-slate-400 shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSelectedIndex(0);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Search live demos, services, pricing, or actions..."
                  className="w-full bg-transparent text-sm sm:text-base font-medium text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => {
                      setQuery("");
                      inputRef.current?.focus();
                    }}
                    className="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <kbd className="hidden sm:inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                  ESC
                </kbd>
              </div>

              {/* Category Filter Pills */}
              <div className="flex items-center gap-1.5 px-4 py-2.5 bg-slate-50/70 dark:bg-slate-950/40 border-b border-slate-200/80 dark:border-slate-800/80 overflow-x-auto text-xs">
                {["All", "Demos", "Services", "Pages", "Actions"].map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => {
                      setSelectedCategory(cat);
                      setSelectedIndex(0);
                    }}
                    className={`px-2.5 py-1 rounded-full font-semibold transition-all cursor-pointer ${
                      selectedCategory === cat
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-slate-600 dark:text-slate-400 hover:bg-slate-200/70 dark:hover:bg-slate-800"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* Search Results List */}
              <div
                ref={listRef}
                className="max-h-[380px] overflow-y-auto overscroll-contain p-2 space-y-1 divide-y divide-slate-100 dark:divide-slate-800/40"
              >
                {filteredItems.length > 0 ? (
                  filteredItems.map((item, idx) => {
                    const isSelected = idx === selectedIndex;
                    return (
                      <div
                        key={item.id}
                        onClick={() => executeItem(item)}
                        onMouseEnter={() => setSelectedIndex(idx)}
                        className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-colors ${
                          isSelected
                            ? "bg-blue-50 dark:bg-blue-950/40 text-blue-950 dark:text-blue-100"
                            : "hover:bg-slate-50 dark:hover:bg-slate-800/60 text-slate-800 dark:text-slate-200"
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div
                            className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border ${
                              isSelected
                                ? "bg-white dark:bg-slate-800 border-blue-300 dark:border-blue-700 shadow-sm"
                                : "bg-slate-100 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700"
                            }`}
                          >
                            {item.icon}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs sm:text-sm font-semibold truncate">
                                {item.title}
                              </span>
                              <span
                                className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${
                                  item.category === "Demos"
                                    ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                                    : item.category === "Services"
                                    ? "bg-blue-500/15 text-blue-600 dark:text-blue-400"
                                    : item.category === "Actions"
                                    ? "bg-purple-500/15 text-purple-600 dark:text-purple-400"
                                    : "bg-slate-200/60 dark:bg-slate-800 text-slate-500"
                                }`}
                              >
                                {item.category}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
                              {item.description}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 pl-3">
                          {isSelected && (
                            <CornerDownLeft className="w-3.5 h-3.5 text-blue-500 dark:text-cyan-400 animate-in fade-in duration-150" />
                          )}
                          <ArrowRight className="w-4 h-4 text-slate-400 opacity-40 group-hover:opacity-100" />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <EmptyState
                    compact
                    title="No matching results"
                    description={`No items matched "${query}". Try searching for real estate, gym, pricing, or custom software.`}
                    secondaryActionLabel="Clear Search"
                    onSecondaryAction={() => {
                      setQuery("");
                      inputRef.current?.focus();
                    }}
                  />
                )}
              </div>

              {/* Footer Hotkey Guide */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 dark:bg-slate-950/70 border-t border-slate-200 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400">
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-mono">↑</kbd>
                    <kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-mono">↓</kbd>
                    <span>Navigate</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-mono">↵</kbd>
                    <span>Select</span>
                  </span>
                </div>
                <span className="text-[10px] text-slate-400">Johnson Softwares Quick Navigator</span>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}

/**
 * Trigger button to embed inside the Navbar
 */
export function CommandKTrigger({ className = "" }: { className?: string }) {
  const handleClick = () => {
    window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }));
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Open Command Search (⌘K)"
      title="Open Command Search (⌘K)"
      className={`group flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-slate-100/70 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 hover:border-blue-500/40 hover:text-slate-800 dark:hover:text-slate-200 transition-all cursor-pointer ${className}`}
    >
      <Search className="w-3.5 h-3.5 group-hover:text-blue-500 dark:group-hover:text-cyan-400 transition-colors" />
      <span className="text-xs font-medium hidden lg:inline">Search...</span>
      <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-400">
        ⌘K
      </kbd>
    </button>
  );
}
